import { AlertCircle, ChevronDown, ChevronRight, FileText } from "lucide-react";
import React from "react";
import { RequirementBadges } from "./RequirementBadges";
import { RequirementDetail } from "./RequirementDetail";
import { SeverityChip } from "./SeverityChip";
import type { Requirement } from "../types/compliance";
import { Badge, Button } from "../components/ui";

function breadcrumb(requirement: Requirement) {
  if (requirement.idsubject === "supplier_service") {
    return `Serviço · ${requirement.nmorigin || "Sem identificação"}`;
  }
  if (requirement.idsubject === "supplier_asset") {
    const scope = requirement.nmscope || "Ativo";
    return `Ativo · ${scope}${
      requirement.idscope ? ` (${requirement.idscope})` : ""
    }`;
  }
  return "Cadastro da empresa";
}

export function RequirementRow({
  requirement,
  compact = false,
  onChanged,
}: {
  requirement: Requirement;
  compact?: boolean;
  onChanged?(): Promise<void> | void;
}) {
  const [open, setOpen] = React.useState(false);
  const required = requirement.fgrequire === 1;

  return (
    <article
      className={`overflow-hidden border-b border-slate-200 last:border-b-0 ${
        required
          ? "border-l-[3px] border-l-slate-800 bg-white"
          : "border-l-[3px] border-l-transparent bg-slate-50"
      }`}
    >
      <Button
        type="button"
        variant="ghost"
        className={`h-auto w-full items-start justify-start rounded-none text-left hover:bg-slate-50 ${
          compact ? "p-3" : "p-4"
        }`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <FileText className="mt-0.5 shrink-0 text-slate-500" size={19} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`text-sm font-medium ${
                required ? "text-slate-900" : "text-slate-600"
              }`}
            >
              {requirement.nmrequirement}
            </span>
            {compact ? (
              <Badge
                variant="outline"
                className="rounded-full border-slate-200 bg-slate-100 text-slate-500"
              >
                Documento
              </Badge>
            ) : (
              <RequirementBadges requirement={requirement} />
            )}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {breadcrumb(requirement)}
          </p>
          {requirement.txrejectionreason && (
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              <AlertCircle className="mt-0.5 shrink-0" size={14} />
              <span>
                <strong>Motivo da reprovação:</strong>{" "}
                {requirement.txrejectionreason}
              </span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <SeverityChip
            fgseverity={requirement.fgseverity}
            fgstatus={requirement.fgstatus}
            fgrequire={requirement.fgrequire}
            nrfiles={requirement.nrfiles}
          />
          {open ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
        </div>
      </Button>
      {open && (
        <RequirementDetail requirement={requirement} onChanged={onChanged} />
      )}
    </article>
  );
}
